import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MdArrowBack, MdSave, MdDownload, MdTableChart,
    MdCheckCircle, MdEditNote, MdCalendarToday
} from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

function getTodayString() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

export default function CompanyManualDataPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const companyId = user?.companyId;
    const today = getTodayString();

    const [company, setCompany] = useState(null);
    const [config, setConfig] = useState({ enabled: false, fields: [] });
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [values, setValues] = useState({});     // { fieldName: inputValue }
    const [saving, setSaving] = useState({});     // { fieldName: bool }
    const [savedToday, setSavedToday] = useState({}); // { fieldName: bool }

    const fetchData = useCallback(async () => {
        if (!companyId) return;
        try {
            setLoading(true);
            const [compRes, entriesRes] = await Promise.all([
                api.get(`/companies/${companyId}`),
                api.get(`/manual-data/${companyId}`)
            ]);
            const comp = compRes.data.data;
            setCompany(comp);
            const cfg = comp?.manualDataConfig;
            setConfig({
                enabled: cfg?.enabled ?? false,
                fields: Array.isArray(cfg?.fields) ? cfg.fields : []
            });
            const allEntries = Array.isArray(entriesRes.data.data) ? entriesRes.data.data : [];
            setEntries(allEntries);

            // Pre-fill today's existing values
            const preValues = {};
            const preSaved = {};
            allEntries.filter(e => e.date === today).forEach(e => {
                preValues[e.fieldName] = String(e.value);
                preSaved[e.fieldName] = true;
            });
            setValues(preValues);
            setSavedToday(preSaved);
        } catch {
            toast.error(t('manualData.dataLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [companyId, today, t]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSaveField = async (fieldName) => {
        const val = values[fieldName];
        if (val === '' || val === undefined || isNaN(Number(val))) {
            return toast.error(t('companyManualData.invalidNumber'));
        }
        try {
            setSaving(s => ({ ...s, [fieldName]: true }));
            await api.post(`/manual-data/${companyId}/entry`, {
                fieldName, value: Number(val), date: today
            });
            setSavedToday(s => ({ ...s, [fieldName]: true }));
            toast.success(t('companyManualData.submitted'));
            // Refresh entries silently
            const res = await api.get(`/manual-data/${companyId}`);
            setEntries(Array.isArray(res.data.data) ? res.data.data : []);
        } catch (err) {
            toast.error(err.response?.data?.message || t('companyManualData.submitFailed'));
        } finally {
            setSaving(s => ({ ...s, [fieldName]: false }));
        }
    };

    const handleDownload = () => {
        if (entries.length === 0) return toast.error(t('manualData.noDownloadData'));

        // Pivot: her parametre ayrı sütun, her tarih ayrı satır
        const allDates = [...new Set(entries.map(e => e.date))].sort((a, b) => b.localeCompare(a));

        // { date -> { fieldName -> value } }
        const byDate = {};
        entries.forEach(e => {
            if (!byDate[e.date]) byDate[e.date] = {};
            byDate[e.date][e.fieldName] = e.value;
        });

        const rows = allDates.map(date => {
            const row = {};
            row[t('common.date')] = date;
            fields.forEach(f => { row[f] = byDate[date]?.[f] ?? ''; });
            return row;
        });

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, t('manualData.title'));
        XLSX.writeFile(wb, `${company?.name || 'firma'}_${t('manualData.title').toLowerCase().replace(/\s+/g, '_')}.xlsx`);
    };

    // Group entries by fieldName
    const grouped = {};
    config.fields.forEach(f => { grouped[f] = []; });
    entries.forEach(e => {
        if (!grouped[e.fieldName]) grouped[e.fieldName] = [];
        grouped[e.fieldName].push(e);
    });

    const fields = config.fields;
    const allTodaySaved = fields.length > 0 && fields.every(f => savedToday[f]);

    if (loading) return <div className="loading-page"><div className="spinner" style={{ width: 48, height: 48 }} /></div>;

    return (
        <div className="page-wrapper">
            {/* ── Header ── */}
            <div className="page-header" style={{ marginBottom: 32, alignItems: 'center' }}>
                <div className="page-header-left" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button className="btn btn-secondary btn-icon" onClick={() => navigate('/company')} id="back-to-dashboard-btn">
                        <MdArrowBack />
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ padding: 12, borderRadius: 12, background: 'rgba(79,142,247,0.12)', color: 'var(--primary)' }}>
                            <MdEditNote size={26} />
                        </div>
                        <div>
                            <h1 className="page-title" style={{ margin: 0, fontSize: 24 }}>{t('companyManualData.title')}</h1>
                            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
                                <MdCalendarToday size={13} />
                                {t('common.today')}: <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)', marginLeft: 4 }}>{today}</span>
                            </p>
                        </div>
                    </div>
                </div>
                <div className="page-header-actions">
                    {entries.length > 0 && (user.isOwner || user.permissions?.canDownloadData) && (
                        <button className="btn btn-secondary btn-sm" onClick={handleDownload} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <MdDownload /> {t('sensorDetail.exportExcel')}
                        </button>
                    )}
                </div>
            </div>

            {/* ── All saved banner ── */}
            <AnimatePresence>
                {allTodaySaved && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        style={{
                            padding: '14px 20px', borderRadius: 12, marginBottom: 24,
                            background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)',
                            color: 'var(--success)', display: 'flex', gap: 10, alignItems: 'center',
                            fontWeight: 600, fontSize: 14
                        }}
                    >
                        <MdCheckCircle size={20} /> {t('companyManualData.allSavedToday')}
                    </motion.div>
                )}
            </AnimatePresence>

            {fields.length === 0 ? (
                <div className="card" style={{ padding: '60px 40px', textAlign: 'center' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
                    <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--text-primary)', marginBottom: 8 }}>{t('manualData.noParamsTitle')}</div>
                    <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>{t('companyManualData.contactAdmin')}</div>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>

                    {/* ── Left: Today's inputs ── */}
                    <div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 14 }}>
                            {t('companyManualData.todayParams', { date: today })}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {fields.map((field, fi) => (
                                <motion.div
                                    key={field}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: fi * 0.07 }}
                                    className="card"
                                    style={{
                                        padding: 20,
                                        border: savedToday[field]
                                            ? '1px solid rgba(34,197,94,0.3)'
                                            : '1px solid var(--border)',
                                        background: savedToday[field]
                                            ? 'rgba(34,197,94,0.04)'
                                            : 'var(--bg-card)',
                                        transition: 'all 0.3s'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 7 }}>
                                                {savedToday[field] && <MdCheckCircle style={{ color: 'var(--success)', fontSize: 17 }} />}
                                                {field}
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                                                {savedToday[field] ? t('companyManualData.todayUpdated') : t('companyManualData.todayNotEntered')}
                                            </div>
                                        </div>
                                        <span style={{
                                            fontSize: 10, padding: '3px 9px', borderRadius: 999, fontWeight: 700,
                                            background: savedToday[field] ? 'rgba(34,197,94,0.12)' : 'var(--bg-body)',
                                            color: savedToday[field] ? 'var(--success)' : 'var(--text-muted)',
                                            border: `1px solid ${savedToday[field] ? 'rgba(34,197,94,0.25)' : 'var(--border)'}`
                                        }}>
                                            {savedToday[field] ? t('companyManualData.savedBadge') : t('companyManualData.pendingBadge')}
                                        </span>
                                    </div>

                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                        <input
                                            className="form-input"
                                            type="number"
                                            placeholder={t('companyManualData.enterValue')}
                                            value={values[field] ?? ''}
                                            onChange={e => setValues(v => ({ ...v, [field]: e.target.value }))}
                                            onKeyDown={e => { if (e.key === 'Enter') handleSaveField(field); }}
                                            id={`manual-field-${field.replace(/\s+/g, '-')}`}
                                            style={{ flex: 1, fontSize: 16 }}
                                        />
                                        <button
                                            className={`btn btn-sm ${savedToday[field] ? 'btn-secondary' : 'btn-primary'}`}
                                            onClick={() => handleSaveField(field)}
                                            disabled={saving[field]}
                                            style={{ height: 42, padding: '0 18px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}
                                        >
                                            {saving[field]
                                                ? <span className="spinner spinner-sm" />
                                                : <><MdSave /> {savedToday[field] ? t('common.edit') : t('common.save')}</>
                                            }
                                        </button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* ── Right: Historical tables ── */}
                    <div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 14 }}>
                            {t('companyManualData.historicalData')}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                            {fields.map((field, fi) => {
                                const fieldEntries = (grouped[field] || []).sort((a, b) => b.date.localeCompare(a.date));
                                return (
                                    <motion.div
                                        key={`hist-${field}`}
                                        className="card"
                                        style={{ padding: 0, overflow: 'hidden' }}
                                        initial={{ opacity: 0, x: 16 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: fi * 0.07 }}
                                    >
                                        <div style={{
                                            padding: '14px 20px', borderBottom: '1px solid var(--border)',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            background: 'rgba(79,142,247,0.04)'
                                        }}>
                                            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: 7 }}>
                                                <MdTableChart /> {field}
                                            </div>
                                            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                                                {fieldEntries.length} {t('common.records')}
                                            </span>
                                        </div>

                                        {fieldEntries.length === 0 ? (
                                            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                                                {t('common.noData')}
                                            </div>
                                        ) : (
                                            <div style={{ maxHeight: 260, overflowY: 'auto', overflowX: 'auto' }}>
                                                <table className="table" style={{ margin: 0 }}>
                                                    <thead>
                                                        <tr>
                                                            <th>{t('common.date')}</th>
                                                            <th>{t('manualData.parameterLabel')}</th>
                                                            <th>{t('common.value')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {fieldEntries.map(e => (
                                                            <tr key={e._id} style={{
                                                                background: e.date === today ? 'rgba(79,142,247,0.05)' : undefined
                                                            }}>
                                                                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                                    {e.date}
                                                                    {e.date === today && (
                                                                        <span style={{
                                                                            marginLeft: 6, fontSize: 9,
                                                                            background: 'rgba(79,142,247,0.18)',
                                                                            color: 'var(--primary)', padding: '1px 6px',
                                                                            borderRadius: 4, fontWeight: 700
                                                                        }}>{t('common.today').toUpperCase()}</span>
                                                                    )}
                                                                </td>
                                                                <td style={{ fontWeight: 600, fontSize: 13 }}>{e.fieldName}</td>
                                                                <td style={{ fontWeight: 700, color: 'var(--success)', fontFamily: 'monospace' }}>{e.value}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
